<template>
  <transition name="modal">
    <div class="modal-overlay">
      <div
        class="modal-wrapper showSweetAlert visible bulk-wrapper"
        data-animation="pop">

        <question v-if="displayQuestion"></question>

        <div class="modal-close col-sm-12 text-right">
          <button class="modal-button" @click="close">
            <span class="icon-close"></span>
          </button>
        </div>

        <header class="modal-header">
          <h2 class="modal-title">Redeem/approve all open notes</h2>
        </header>

        <div class="modal-body mt-lg">

        <spinner v-if="loading" :text="loadingText" inline></spinner>

        <!-- ============ Result summary ============ -->
        <div v-else-if="result">
          <div
            class="alert"
            :class="result.failed ? 'alert-warning' : 'alert-success'"
            role="alert">
            <h4 v-if="!result.failed">{{ result.title }}</h4>
            <h4 v-else>{{ result.title }} &mdash; with failures</h4>
            <p>{{ result.message }}</p>
          </div>

          <div v-if="result.failed" class="failures-block">
            <p class="text-muted">
              These notes were not processed and must be handled individually:
            </p>
            <ul class="failure-list">
              <li v-for="failure in result.failures" :key="failure.noteId">
                Note #{{ failure.noteId }} &mdash; {{ failure.errorMessage }}
              </li>
            </ul>
          </div>

          <div class="modal-buttons mt-lg">
            <div class="row">
              <div class="col-md-12 text-right">
                <button type="button" class="btn btn-info btn-lg btn-accept" @click="close">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ Approval (second admin) ============ -->
        <!-- Shows the request as it was made, not just its size: the approver
             has to check the destination, the fee, the notes and the
             requester's comment, the same way the single-note redeem screen
             presents what it is approving. -->
        <div v-else-if="isApproval">
          <div class="alert alert-info" role="alert">
            <p>
              A bulk redemption request for
              <strong>{{ openBatch.noteCount }}</strong> notes is awaiting
              approval on this account. Check the details below before
              approving.
            </p>
          </div>

          <div class="row">
            <div class="col-md-6">
              <fieldset class="form-group">
                <label>Bank account</label>
                <p class="form-control-static">{{ requestFundingSourceLabel }}</p>
              </fieldset>
            </div>
            <div class="col-md-6">
              <fieldset class="form-group">
                <label>Service fee (whole batch)</label>
                <p class="form-control-static">
                  {{ formatAmount(openBatch.serviceFee || 0) }}
                  <span class="text-muted" v-if="requestFeeBearer">
                    &mdash; charged once, to note {{ requestFeeBearer.noteNumber }}
                  </span>
                </p>
              </fieldset>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <fieldset class="form-group">
                <label>Redemption category</label>
                <p class="form-control-static">{{ requestCategoryLabel }}</p>
              </fieldset>
            </div>
          </div>

          <!-- Totals -->
          <div class="totals-block">
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Notes to redeem</span></div>
              <div class="col-md-6 text-right"><strong>{{ openBatch.noteCount }}</strong></div>
            </div>
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Principal</span></div>
              <div class="col-md-6 text-right">
                {{ formatAmount(requestTotals.principal) }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Interest</span></div>
              <div class="col-md-6 text-right">
                {{ formatAmount(requestTotals.interest) }}
              </div>
            </div>
            <div class="row" v-if="requestTotals.fee > 0">
              <div class="col-md-6"><span class="totals-label">Service fee</span></div>
              <div class="col-md-6 text-right text-danger">
                &minus;{{ formatAmount(requestTotals.fee) }}
              </div>
            </div>
            <div class="row total-row">
              <div class="col-md-6"><span class="totals-label">Total paid to investor</span></div>
              <div class="col-md-6 text-right total-amount">
                {{ formatAmount(requestNetPayout) }}
              </div>
            </div>
          </div>

          <p class="toggle-link" v-if="requestNotes.length">
            <a href @click.prevent="showNotes = !showNotes">
              {{ showNotes ? 'Hide' : 'Show' }} the {{ requestNotes.length }} notes
              being redeemed
            </a>
          </p>
          <div v-if="showNotes && requestNotes.length" class="note-list">
            <table class="table table-condensed">
              <thead>
                <tr>
                  <th>Note</th>
                  <th class="text-right">Principal</th>
                  <th class="text-right">Interest</th>
                  <th class="text-right" v-if="requestTotals.fee > 0">Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="note in requestNotes" :key="note.noteId">
                  <td>{{ note.noteNumber }}</td>
                  <td class="text-right">{{ formatAmount(note.principalAmount) }}</td>
                  <td class="text-right">{{ formatAmount(note.interestAmount) }}</td>
                  <td class="text-right" v-if="requestTotals.fee > 0">
                    <span v-if="note.feeAmount > 0" class="text-danger">
                      &minus;{{ formatAmount(note.feeAmount) }}
                    </span>
                    <span v-else class="text-muted">&mdash;</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- One box holding both comments, as the single-note flow does: the
               requester's comment is already in it and the approver appends. -->
          <fieldset class="form-group">
            <label for="bulkApprovalComment">Please append your comment below *</label>
            <textarea
              id="bulkApprovalComment"
              class="form-control"
              rows="4"
              maxlength="255"
              v-model.trim="approvalComment">
            </textarea>
            <p class="text-danger" v-if="!hasAppendedComment">
              <em>You must enter or append a comment.</em>
            </p>
          </fieldset>

          <div class="modal-buttons mt-lg">
            <div class="row">
              <div class="col-md-12 text-right">
                <button type="button" class="btn btn-info btn-lg btn-cancel" @click="close">
                  Close
                </button>
                <button
                  type="button"
                  class="btn btn-info btn-lg btn-discard"
                  :disabled="!hasAppendedComment"
                  @click="confirmCancelBatch">
                  Cancel request
                </button>
                <button
                  type="button"
                  class="btn btn-info btn-lg btn-accept"
                  :disabled="!hasAppendedComment"
                  @click="confirmApproval">
                  Approve all
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ Request (first admin) ============ -->
        <div v-else>
          <div v-if="preview.eligibleCount === 0" class="alert alert-warning" role="alert">
            <p>
              No notes on this account can be bulk redeemed
              <span v-if="preview.ineligibleCount">
                &mdash; all {{ preview.ineligibleCount }} open notes are excluded.
                See the reasons below.
              </span>
            </p>
          </div>

          <div class="row">
            <div class="col-md-6">
              <fieldset class="form-group">
                <label for="bulkFundingSource">Bank account *</label>
                <select
                  id="bulkFundingSource"
                  class="form-control"
                  v-model="fundingSourceId">
                  <option value="">Please select</option>
                  <option
                    v-for="fs in fundingSources"
                    :value="String(fs.id)"
                    :key="fs.id">
                    {{ fs.name }}
                  </option>
                  <option :value="donateFundingSource.name">
                    {{ donateFundingSource.text }}
                  </option>
                </select>
              </fieldset>
            </div>

            <div class="col-md-6">
              <fieldset class="form-group">
                <label for="bulkServiceFee">Service fee (whole batch)</label>
                <input
                  id="bulkServiceFee"
                  class="form-control text-right"
                  v-money="money"
                  v-model="serviceFeeInput"
                  autocomplete="off" />
                <p class="fee-hint" :class="preview.feeError ? 'text-danger' : 'text-muted'">
                  <span v-if="refreshing">Recalculating…</span>
                  <span v-else-if="preview.feeError">{{ preview.feeError }}</span>
                  <span v-else-if="preview.feeBearer">
                    Charged once, to note {{ preview.feeBearer.noteNumber }}
                    ({{ formatAmount(preview.feeBearer.currentBalance) }}) &mdash; the
                    largest note in the batch.
                  </span>
                  <span v-else>
                    Charged once for the whole batch, not per note.
                  </span>
                </p>
              </fieldset>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <fieldset class="form-group">
                <label for="bulkCategory">Redemption category *</label>
                <select id="bulkCategory" class="form-control" v-model="categoryId">
                  <option
                    v-for="rc in preview.redemptionCategories"
                    :value="rc.id"
                    :key="rc.id">
                    {{ rc.label }}
                  </option>
                </select>
              </fieldset>
            </div>

            <div class="col-md-6" v-if="needsCategoryHelperText">
              <fieldset class="form-group">
                <label for="bulkCategoryHelperText">Category detail *</label>
                <input
                  id="bulkCategoryHelperText"
                  class="form-control"
                  v-model.trim="categoryHelperText"
                  placeholder="Enter a redemption category…"
                  autocomplete="off" />
              </fieldset>
            </div>
          </div>

          <!-- Totals -->
          <div class="totals-block">
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Notes to redeem</span></div>
              <div class="col-md-6 text-right">
                <strong>{{ preview.eligibleCount }}</strong>
                <span class="text-muted" v-if="preview.ineligibleCount">
                  ({{ preview.ineligibleCount }} excluded)
                </span>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Principal</span></div>
              <div class="col-md-6 text-right">{{ formatAmount(preview.totals.principal) }}</div>
            </div>
            <div class="row">
              <div class="col-md-6"><span class="totals-label">Interest</span></div>
              <div class="col-md-6 text-right">{{ formatAmount(preview.totals.interest) }}</div>
            </div>
            <div class="row" v-if="preview.totals.fee > 0">
              <div class="col-md-6">
                <span class="totals-label">
                  Service fee
                  <span v-if="preview.feeBearer">
                    (on note {{ preview.feeBearer.noteNumber }})
                  </span>
                </span>
              </div>
              <div class="col-md-6 text-right text-danger">
                &minus;{{ formatAmount(preview.totals.fee) }}
              </div>
            </div>
            <div class="row total-row">
              <div class="col-md-6"><span class="totals-label">Total paid to investor</span></div>
              <div class="col-md-6 text-right total-amount">
                {{ formatAmount(preview.totals.netPayout) }}
              </div>
            </div>
          </div>

          <fieldset class="form-group">
            <label for="bulkComment">Please append your comment *</label>
            <textarea
              id="bulkComment"
              class="form-control"
              rows="2"
              maxlength="255"
              v-model.trim="comment"></textarea>
          </fieldset>

          <!-- Note lists -->
          <p class="toggle-link">
            <a href @click.prevent="showNotes = !showNotes">
              {{ showNotes ? 'Hide' : 'Show' }} the {{ preview.eligibleCount }} notes
              being redeemed
            </a>
          </p>
          <div v-if="showNotes" class="note-list">
            <table class="table table-condensed">
              <thead>
                <tr>
                  <th>Note</th>
                  <th class="text-right">Principal</th>
                  <th class="text-right">Interest</th>
                  <th class="text-right" v-if="preview.totals.fee > 0">Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="note in preview.eligibleNotes" :key="note.noteId">
                  <td>{{ note.noteNumber }}</td>
                  <td class="text-right">{{ formatAmount(note.currentBalance) }}</td>
                  <td class="text-right">{{ formatAmount(note.accruedInterest) }}</td>
                  <!-- Only one note carries the batch fee; the rest show a dash so
                       that is obvious at a glance rather than reading as $0.00. -->
                  <td class="text-right" v-if="preview.totals.fee > 0">
                    <span v-if="note.serviceFee > 0" class="text-danger">
                      &minus;{{ formatAmount(note.serviceFee) }}
                    </span>
                    <span v-else class="text-muted">&mdash;</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="toggle-link" v-if="preview.ineligibleCount">
            <a href @click.prevent="showExcluded = !showExcluded">
              {{ showExcluded ? 'Hide' : 'Show' }} the {{ preview.ineligibleCount }} excluded
              notes
            </a>
          </p>
          <div v-if="showExcluded" class="note-list">
            <table class="table table-condensed">
              <thead>
                <tr>
                  <th>Note</th>
                  <th class="text-right">Principal</th>
                  <th>Why it is excluded</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="note in preview.ineligibleNotes" :key="note.noteId">
                  <td>{{ note.noteNumber }}</td>
                  <td class="text-right">{{ formatAmount(note.currentBalance) }}</td>
                  <td class="text-muted">{{ note.ineligibilityText }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="modal-buttons mt-lg">
            <div class="row">
              <div class="col-md-12 text-right">
                <button type="button" class="btn btn-info btn-lg btn-cancel" @click="close">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-info btn-lg btn-accept"
                  :disabled="!canSubmit"
                  @click="confirmSubmit">
                  Redeem {{ preview.eligibleCount }} notes
                </button>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex';
  import { VMoney } from 'v-money';

  import {
    HIDE_QUESTION_MODAL,
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
    SHOW_QUESTION_MODAL,
  } from '@/store/mutation-types';
  import { formatAmount } from '@/util';
  import Actions from '@/data/static/upcomingTransactionActions';
  import Question from '@/components/modals/Question';
  import spinner from '@/components/widgets/Spinner';

  /** How often the batch status is polled while a phase runs in the background. */
  const POLL_INTERVAL_MS = 2000;
  /** Stop polling after this long so a stalled batch cannot spin forever. */
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;
  /** Settle time after the admin stops typing before re-pricing the batch. */
  const FEE_DEBOUNCE_MS = 500;

  const emptyPreview = () => ({
    eligibleCount: 0,
    ineligibleCount: 0,
    eligibleNotes: [],
    ineligibleNotes: [],
    redemptionCategories: [],
    serviceFee: 0,
    feeBearer: null,
    feeError: null,
    totals: { principal: 0, interest: 0, fee: 0, netPayout: 0 },
    openBatch: null,
    fundingSources: [],
  });

  export default {
    name: 'bulk-redemption',
    components: {
      question: Question,
      spinner,
    },
    props: ['accountId'],
    directives: { money: VMoney },
    data() {
      return {
        loading: true,
        loadingText: 'Loading…',
        refreshing: false,
        preview: emptyPreview(),
        fundingSourceId: '',
        serviceFeeInput: '0.00',
        categoryId: null,
        categoryHelperText: '',
        comment: '',
        approvalComment: '',
        // The requester's comment as it arrived, so the approver's box can be
        // pre-filled with it and still be told apart from an untouched one.
        requestComment: '',
        showNotes: false,
        showExcluded: false,
        result: null,
        pollTimer: null,
        feeTimer: null,
        money: { decimal: '.', thousands: ',', prefix: '$', precision: 2 },
      };
    },
    beforeMount() {
      this.loadPreview();
    },
    beforeDestroy() {
      this.stopPolling();
      this.stopFeeTimer();
      // The question modal lives in the store, so an open confirmation would
      // outlive this modal and strand the user behind it.
      this.hideQuestionModal();
    },
    watch: {
      /**
       * Re-prices the batch when the fee changes.
       *
       * The fee no longer decides which notes qualify, but it still decides
       * which note bears it and whether any note can, so the server re-prices
       * rather than the modal guessing.
       *
       * Watched rather than bound to @change because the v-money directive
       * dispatches a synthetic change event every time it binds. Combined with a
       * request that re-renders this input, that produced an endless
       * refresh loop. Guarding on the parsed value means a re-bind that does not
       * actually change the number is ignored.
       */
      serviceFeeInput(next, previous) {
        if (this.unformat(next) === this.unformat(previous)) return;

        this.stopFeeTimer();
        this.feeTimer = setTimeout(() => {
          if (this.unformat(this.serviceFeeInput) === this.preview.serviceFee) {
            return;
          }
          this.loadPreview({ silent: true });
        }, FEE_DEBOUNCE_MS);
      },
    },
    computed: {
      ...mapState('question', { displayQuestion: 'display' }),
      donateFundingSource() {
        return Actions.donate;
      },
      openBatch() {
        return this.preview.openBatch || {};
      },
      /** A batch already awaiting approval turns this modal into the approve step. */
      isApproval() {
        return Boolean(
          this.preview.openBatch &&
            this.preview.openBatch.status === 'PENDING_APPROVAL',
        );
      },
      fundingSources() {
        return (this.preview.fundingSources || []).filter(
          fs =>
            ['bank', 'check', 'wire/ach'].indexOf(fs.type) !== -1 &&
            fs.status === 'verified' &&
            !fs.removed,
        );
      },
      needsCategoryHelperText() {
        const category = (this.preview.redemptionCategories || []).find(
          rc => rc.id === this.categoryId,
        );
        return Boolean(category && category.needsHelperText);
      },
      /** The notes the open request named, from its own snapshot. */
      requestNotes() {
        return this.openBatch.notes || [];
      },
      requestTotals() {
        return this.openBatch.totals || { principal: 0, interest: 0, fee: 0 };
      },
      requestNetPayout() {
        const totals = this.requestTotals;
        return (totals.principal + totals.interest) - totals.fee;
      },
      /** The note carrying the batch fee, for the caption beside the amount. */
      requestFeeBearer() {
        return this.requestNotes.find(note => note.feeAmount > 0) || null;
      },
      /**
       * The request's payment destination, named rather than shown as an id.
       *
       * Donations are stored in the same column as a funding source id, so the
       * donate sentinel is checked before looking the id up.
       */
      requestFundingSourceLabel() {
        const id = this.openBatch.fundingSourceId;
        if (!id) return 'Not recorded';
        if (id === this.donateFundingSource.name) {
          return this.donateFundingSource.text;
        }
        const match = (this.preview.fundingSources || []).find(
          fs => String(fs.id) === String(id),
        );
        return match ? match.name : `Funding source ${id}`;
      },
      requestCategoryLabel() {
        const category = (this.preview.redemptionCategories || []).find(
          rc => rc.id === this.openBatch.redemptionCategoryId,
        );
        if (!category) return 'Not recorded';
        return this.openBatch.redemptionCategoryHelperText
          ? `${category.label} — ${this.openBatch.redemptionCategoryHelperText}`
          : category.label;
      },
      /**
       * Whether the approver has actually added something.
       *
       * Matches the server rule and the single-note screen: the box starts with
       * the requester's comment, so leaving it untouched is not an approval.
       */
      hasAppendedComment() {
        return Boolean(
          this.approvalComment && this.approvalComment !== this.requestComment,
        );
      },
      canSubmit() {
        if (!this.preview.eligibleCount) return false;
        // A fee no note can carry is rejected server side; block it here so the
        // admin fixes the amount instead of getting a 400.
        if (this.preview.feeError) return false;
        if (!this.fundingSourceId) return false;
        if (!this.categoryId) return false;
        if (this.needsCategoryHelperText && !this.categoryHelperText) return false;
        if (!this.comment) return false;
        return true;
      },
    },
    methods: {
      formatAmount,
      ...mapActions('note', [
        'previewBulkRedemption',
        'createBulkRedemption',
        'getBulkRedemption',
        'approveBulkRedemption',
        'cancelBulkRedemption',
      ]),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('question', {
        showQuestionModal: SHOW_QUESTION_MODAL,
        hideQuestionModal: HIDE_QUESTION_MODAL,
      }),
      unformat(value) {
        const raw = String(value).replace('$', '').replace(/,/g, '');
        const parsed = parseFloat(raw);
        return Number.isNaN(parsed) ? 0 : parsed;
      },
      /**
       * `silent` keeps the form mounted while re-pricing. Swapping it for the
       * spinner would destroy and rebuild the fee input, and the v-money
       * directive fires a change event on every bind.
       */
      async loadPreview({ silent = false } = {}) {
        if (silent) {
          this.refreshing = true;
        } else {
          this.loading = true;
          this.loadingText = 'Loading…';
        }

        try {
          const preview = await this.previewBulkRedemption({
            accountId: this.accountId,
            serviceFee: this.unformat(this.serviceFeeInput),
          });
          this.preview = Object.assign(emptyPreview(), preview);
          if (!this.categoryId && this.preview.redemptionCategories.length) {
            this.categoryId = this.preview.redemptionCategories[0].id;
          }
          this.seedApprovalComment();
        } catch (error) {
          this.fail(
            this.errorText(error, 'We could not load the bulk redemption preview.'),
          );
        } finally {
          this.loading = false;
          this.refreshing = false;
        }
      },
      stopFeeTimer() {
        if (this.feeTimer) {
          clearTimeout(this.feeTimer);
          this.feeTimer = null;
        }
      },
      /**
       * Puts the requester's comment in the approver's box, once.
       *
       * Only on the first load that finds a request awaiting approval: a later
       * reload must not overwrite what the approver has typed.
       */
      seedApprovalComment() {
        const batch = this.preview.openBatch;
        if (!batch || !batch.requestComment) return;
        if (this.requestComment) return;

        this.requestComment = batch.requestComment;
        this.approvalComment = batch.requestComment;
      },
      confirmSubmit() {
        const total = formatAmount(this.preview.totals.netPayout);
        this.askToConfirm({
          title: 'Request this bulk redemption?',
          question:
            `This redeems ${this.preview.eligibleCount} notes and pays ` +
            `${total} to the investor. Another admin must approve it before ` +
            'any money moves.',
          yesButtonText: 'Request',
          method: this.submit,
        });
      },
      async submit() {
        this.loading = true;
        this.loadingText = 'Requesting redemptions…';
        try {
          const { batchId } = await this.createBulkRedemption({
            accountId: this.accountId,
            payload: {
              fundingSourceId: String(this.fundingSourceId),
              serviceFee: this.unformat(this.serviceFeeInput),
              comment: this.comment,
              categoryId: this.categoryId,
              categoryHelperText: this.categoryHelperText,
              noteIds: this.preview.eligibleNotes.map(note => note.noteId),
              expectedTotalPrincipal: this.preview.totals.principal,
            },
          });
          await this.pollUntilSettled(batchId, 'CREATING', 'Redemption requested');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not submit the bulk redemption.'));
        }
      },
      confirmApproval() {
        this.askToConfirm({
          title: 'Approve this bulk redemption?',
          question:
            `This approves the redemption of ${this.openBatch.noteCount} ` +
            'notes and releases the payment. It cannot be undone here.',
          yesButtonText: 'Approve',
          method: this.approve,
        });
      },
      confirmCancelBatch() {
        this.askToConfirm({
          title: 'Withdraw this bulk redemption?',
          question:
            `This withdraws the request for all ${this.openBatch.noteCount} ` +
            'notes. Nothing is redeemed and the account is left as it was.',
          yesButtonText: 'Withdraw',
          method: this.cancelBatch,
        });
      },
      /**
       * Asks the shared question modal, then runs the action on confirm.
       *
       * The modal is the app's own confirmation dialog; window.confirm was a
       * placeholder and is blocked outright in some embedded contexts.
       */
      askToConfirm({ title, question, yesButtonText, method }) {
        this.showQuestionModal({
          title,
          question,
          yesButtonText,
          hideCancelButton: false,
          method: () => {
            this.hideQuestionModal();
            method();
          },
        });
      },
      async approve() {
        this.loading = true;
        this.loadingText = 'Redeeming notes…';
        try {
          await this.approveBulkRedemption({
            accountId: this.accountId,
            batchId: this.openBatch.id,
            payload: { comment: this.approvalComment },
          });
          await this.pollUntilSettled(this.openBatch.id, 'APPROVING', 'Redemptions approved');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not approve the bulk redemption.'));
        }
      },
      async cancelBatch() {
        this.loading = true;
        this.loadingText = 'Withdrawing requests…';
        try {
          const { cancelledNotes } = await this.cancelBulkRedemption({
            accountId: this.accountId,
            batchId: this.openBatch.id,
            payload: { comment: this.approvalComment },
          });
          this.loading = false;
          this.result = {
            title: 'Request cancelled',
            message: `${cancelledNotes} note redemption request(s) were cancelled.`,
            failed: false,
            failures: [],
          };
          this.$emit('completed');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not cancel the bulk redemption.'));
        }
      },
      /**
       * Both phases return 202 and finish in the background, so the modal polls
       * the batch until it leaves the in-progress status.
       */
      async pollUntilSettled(batchId, inProgressStatus, successTitle) {
        const startedAt = Date.now();

        const check = async () => {
          let batch;
          try {
            batch = await this.getBulkRedemption({
              accountId: this.accountId,
              batchId,
            });
          } catch (error) {
            this.fail('We lost track of this batch. Reopen to see its status.');
            return;
          }

          if (batch.status === inProgressStatus) {
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              this.fail(
                'This is taking longer than expected. Reopen the modal to check progress.',
              );
              return;
            }
            this.pollTimer = setTimeout(check, POLL_INTERVAL_MS);
            return;
          }

          this.stopPolling();
          this.loading = false;

          // Which count means "processed" depends on the phase: requesting
          // leaves notes awaiting approval, approving redeems them.
          const done =
            inProgressStatus === 'CREATING'
              ? batch.progress.requested
              : batch.progress.done;
          const failedSuffix = batch.progress.failed
            ? ` ${batch.progress.failed} failed.`
            : '';
          this.result = {
            title: successTitle,
            message: `${done} of ${batch.noteCount} notes processed.${failedSuffix}`,
            failed: batch.progress.failed > 0,
            failures: batch.failures || [],
          };
          this.$emit('completed');
        };

        await check();
      },
      stopPolling() {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
      },
      errorText(error, fallback) {
        const message =
          error && error.response && error.response.data && error.response.data.message;
        return message || fallback;
      },
      fail(message) {
        this.stopPolling();
        this.stopFeeTimer();
        this.loading = false;
        this.refreshing = false;
        this.setErrorsModalInfo([message]);
        this.showErrorsModal();
      },
      close() {
        this.stopPolling();
        this.stopFeeTimer();
        this.$emit('close');
      },
    },
  };
</script>

<style scoped>
  /* base.scss sets .modal-overlay/.modal-wrapper to display:none and caps the
     wrapper at 580px. Overridden here the same way AddMoney.vue does, rather
     than with the inline style="display: block" the older modals use. Wider
     than the standard modal because the note tables need the room. */
  .modal-overlay {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: auto;
    padding: 15px;
    padding-top: 80px;
    -webkit-overflow-scrolling: touch;
  }

  .modal-wrapper.bulk-wrapper {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    display: block;
    margin: auto;
    flex-shrink: 0;
    width: 90%;
    max-width: 820px;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Matches the summary panels used on the redeem screen. */
  .totals-block {
    background: #f7f9fa;
    border-radius: 4px;
    padding: 15px 20px;
    margin: 20px 0;
  }
  .totals-label {
    color: #888;
    font-size: 14px;
  }
  .total-row {
    border-top: 1px solid #d3d3d3;
    margin-top: 8px;
    padding-top: 8px;
  }
  .total-amount {
    font-size: 20px;
    font-weight: 600;
    color: #4a4a4a;
  }

  .fee-hint {
    font-size: 12px;
    margin-top: 4px;
  }

  .toggle-link {
    margin: 10px 0 4px 0;
  }
  .toggle-link a {
    color: #127285;
    cursor: pointer;
  }

  .note-list {
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 12px;
  }
  .note-list table {
    margin-bottom: 0;
  }

  .failures-block {
    margin-bottom: 15px;
  }
  .failure-list {
    max-height: 220px;
    overflow-y: auto;
    padding-left: 18px;
  }

  /* Destructive action: distinct from the teal cancel and green accept so an
     admin cannot confuse "cancel this request" with "close this dialog". */
  .modal-wrapper .btn-discard {
    background-color: #f05050;
    border-color: #f05050;
    color: #ffffff;
    transition: all .4s ease-in-out;
  }
  .modal-wrapper .btn-discard:hover {
    background-color: #d33f3f;
    border-color: #d33f3f;
  }
</style>



// WEBPACK FOOTER //
// BulkRedemption.vue?6122c1e8